import { useEffect, useState } from 'react';
import { FetchResult, useMutation, useQuery } from '@apollo/client';
import { GET_AUTH_USER } from '../views/Accounts/graphql/getAuthUser';
import { PORTAL_DOCUMENT } from '../views/Accounts/graphql/portalDocument';

const METHOD = 'post';
const LIMIT = 100;
const APPLICATION = 'Portal';

enum IDocType {
  IMP_LIST = 'impList',
  USERS_IMP = 'usersImp',
}

interface IGetClientsParams {
  name?: string;
  email?: string;
  offset?: number;
  limit?: number;
}

interface IGetClientsResponse {
  email: string;
  userId: string;
  firstName: string;
  surname: string;
}

interface IImpListResponse {
  email: string;
  hasPermission: boolean;
}

interface IClients {
  data: IGetClientsResponse[];
  isLoading: boolean;
}

interface IImpInfo {
  isLoading: boolean;
  email: string;
  hasPermission: boolean;
}

const defaultClientState: IClients = {
  data: [],
  isLoading: false,
};

const defaultImpInfoState: IImpInfo = {
  email: '',
  hasPermission: false,
  isLoading: false,
};

// portalDocument returns a JSON blob shaped as { data: <payload>, ... }
type PortalDocumentResult = { portalDocument?: { data?: unknown } | null };

const getResponse = <T, B = unknown>(dataIn: FetchResult<PortalDocumentResult>, defaults?: B) => {
  return (dataIn?.data?.portalDocument?.data ?? defaults ?? {}) as T;
};

/**
 * Provides the impersonatable client list and the logged-in RM's impersonation
 * permission. `rmEmail` is the email of the currently authenticated user
 * (from AppContext settings).
 */
export const useImp = (rmEmail?: string) => {
  const [clients, setClients] = useState<IClients>(defaultClientState);
  const [impInfo, setImpInfo] = useState<IImpInfo>(defaultImpInfoState);

  const [impersonationFn] = useMutation<PortalDocumentResult>(PORTAL_DOCUMENT);

  // The RM's email drives the permission check. Prefer the explicitly passed
  // value, otherwise resolve it directly from authUser (settings.email in the
  // app context is populated late and may be empty on first render).
  const { data: authUserData } = useQuery<{ authUser?: { emailAddress?: string } }>(GET_AUTH_USER);
  const email = rmEmail || authUserData?.authUser?.emailAddress || '';

  const getClients = async (paramsIn: IGetClientsParams) => {
    setClients((prev) => ({ ...prev, isLoading: true }));
    const response = await impersonationFn({
      variables: {
        method: METHOD,
        docType: IDocType.USERS_IMP,
        data: { limit: LIMIT, offset: 0, application: APPLICATION, ...paramsIn },
      },
    });

    const results = getResponse<IGetClientsResponse[]>(response, []);
    setClients((prev) => ({ ...prev, data: results, isLoading: false }));
  };

  const getImps = async (rmEmailAddress: string) => {
    setImpInfo((prev) => ({ ...prev, isLoading: true }));
    const response = await impersonationFn({
      variables: {
        method: METHOD,
        docType: IDocType.IMP_LIST,
        data: { email: rmEmailAddress },
      },
    });
    const resp = getResponse<IImpListResponse>(response, {});

    setImpInfo({
      email: resp.email ?? '',
      hasPermission: resp.hasPermission ?? false,
      isLoading: false,
    });
  };

  const onSearch = (searchText: string) => {
    getClients({ limit: LIMIT, name: searchText });
  };

  useEffect(() => {
    if (email) {
      getImps(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return {
    getClients,
    impInfo,
    clients: { ...clients, onSearch },
  };
};
